import { apiClient } from './ApiClient'
import type {
  Startup,
  StartupCreateRequest,
  StartupListResponse,
  StartupWithMembership,
} from '@/types/api'

/**
 * Wraps the /v1/startups surface. Every method returns typed payloads
 * from api.ts; every non-2xx bubbles up as an ApiError (see ApiClient).
 *
 * The shell calls `list()` on first mount via useStartupBootstrap and
 * routes based on the array length:
 *   - `[]`       → /onboarding
 *   - `[single]` → set active, render /overview
 *   - `[many]`   → set first active, log for follow-up (v1 behaviour)
 *
 * `create()` returns both the new `Startup` and the auto-created
 * `StartupMembership` in a single call, so the onboarding flow can
 * seed the Zustand slice immediately without a follow-up GET.
 */
export class StartupService {
  /** GET /v1/startups — startups where the current user has active membership. */
  async list(): Promise<StartupWithMembership[]> {
    const response = await apiClient.get<StartupListResponse>('/v1/startups')
    return response.startups ?? []
  }

  /** POST /v1/startups — creates a startup and returns it with the owner membership. */
  async create(input: StartupCreateRequest): Promise<StartupWithMembership> {
    return apiClient.post<StartupWithMembership>('/v1/startups', input)
  }

  /** GET /v1/startups/{id} — one startup + the caller's membership. */
  async get(startupId: string): Promise<StartupWithMembership> {
    return apiClient.get<StartupWithMembership>(`/v1/startups/${startupId}`)
  }

  /**
   * Convenience accessor for callers that only need the Startup entity,
   * not the wrapper. Most code should prefer the membership-aware returns
   * so role checks can happen without a second call.
   */
  async getEntity(startupId: string): Promise<Startup> {
    const { startup } = await this.get(startupId)
    return startup
  }
}

export const startupService = new StartupService()
